import React, { useEffect, useMemo, useState } from 'react'
import { find } from 'lodash'
import { Skeleton } from 'antd'

import LPHeader from 'src/components/layout/Header/LPHeader'
import { useAppDispatch, useAppSelector } from 'src/store'
import { getPlans } from 'src/useCase/plan'
import LocalStorage from 'src/utils/LocalStorage'
import { getInfo } from 'src/useCase/user'
import PlanInfoComponent from 'src/containers/AutomaticPlan/components/Plan/PlanInfo'
import { PlanInterface } from 'src/interfaces/plan'
import { ProposalDiv } from 'src/containers/AutomaticPlan/Proposal'
import { PLAN_TYPE } from 'src/constants/app'
import { getSelectedPlan } from 'src/useCase/selectedFromLp'
import { getUserTreatmentPlan } from 'src/useCase/userTreatment'
import { useSearchParams } from 'react-router-dom'
import StepsHeader from 'src/components/layout/Header/StepHeader'
import { HeaderStyle } from 'src/components/layout/Header/HeaderStyle'
import MenuNameComponent from 'src/components/common/MenuNameComponent'
import { EMAIL_SUPPORT } from 'src/constants/env-constant'

const ShampooSubscriptionContainer: React.FC = (props: any) => {
  const [showRecommendPlans, setShowRecommendPlans] = useState(false)
  const [activePlan, setActivePlan] = useState<PlanInterface>()

  const dispatch = useAppDispatch()

  const plansWithMenu = useAppSelector(
    (state) => state.domain.plans.plans.entity,
  )
  const plansLoading = useAppSelector(
    (state) => state.domain.plans.plans.loading,
  )
  const accessToken = LocalStorage.accessToken
  const user = useAppSelector((state) => state.ui.app.authInfo)
  const planFromLp = useAppSelector(
    (state) => state.domain.selectedFromLp.selectedDetail.entity,
  )
  const selectedUserTreatmentId = useAppSelector(
    (state) =>
      state.domain.selectedUserTreatmentId.userTreatmentId
        .selectedUserTreatmentId,
  )

  const [searchParams] = useSearchParams()
  const menuId = searchParams.get('menu_id')
  const userTreatmentId = LocalStorage.getUserTreatmentId()
  const [fetchedUser, setFetchedUser] = useState(false)
  // [SCOPE] Mirrors the changedPlanId fetch already used in
  // KartePlan/PrescriptionProposal (Task No.143) so the 変更予定プラン badge
  // also renders on this screen when CS/admin changed the assigned plan.
  const [changedPlanId, setChangedPlanId] = useState<number | null>(null)

  useEffect(() => {
    if (accessToken && !fetchedUser) {
      dispatch(
        getInfo({
          with: 'userTreatments,stateTransitions,userTreatments.reservation.doctor',
        }),
      )
      setFetchedUser(true)
    }
  }, [accessToken, dispatch, fetchedUser])

  useEffect(() => {
    dispatch(
      getPlans({
        menu_id: Number(menuId),
        user_treatment_id: Number(userTreatmentId),
        select_field: 'id,name,price,duration',
      }),
    )

    dispatch(
      getSelectedPlan({
        user_treatment_id: selectedUserTreatmentId || userTreatmentId,
      }),
    )

    dispatch(
      getUserTreatmentPlan({
        id: selectedUserTreatmentId || userTreatmentId,
        params: { with: 'plan' },
      }),
    )
      .unwrap()
      .then((plan) => setChangedPlanId(plan?.id ?? null))
      .catch(() => setChangedPlanId(null))
  }, [menuId, userTreatmentId, selectedUserTreatmentId, dispatch])

  useEffect(() => {
    const activePlanLocal = Number(LocalStorage.activePlan)
    const defaultActivePlan = Array.isArray(plansWithMenu)
      ? find(plansWithMenu, (plan) => plan?.id === activePlanLocal)
      : undefined
    if (defaultActivePlan) {
      setActivePlan(defaultActivePlan)
    }
  }, [plansWithMenu])

  const selectPlan = (plan) => {
    if (plan) {
      LocalStorage.setActivePlan(plan?.id)
      LocalStorage.setPlanType(PLAN_TYPE.BUY_SHAMPOO_SUBSCRIPTION)
      setActivePlan(plan)
    }
  }

  // [SCOPE] Same priority rule as PrescriptionProposal (Task No.143): the
  // admin-changed plan is pulled out of `plans` into `planImportants` so
  // PlanInfo.tsx renders its 変更予定プラン badge for it.
  const changedPlan = useMemo(() => {
    if (changedPlanId == null || !Array.isArray(plansWithMenu)) {
      return null
    }
    if (planFromLp?.id === changedPlanId) {
      return planFromLp
    }
    return plansWithMenu.find((plan) => plan.id === changedPlanId) ?? null
  }, [plansWithMenu, planFromLp, changedPlanId])

  const planImportants = useMemo<PlanInterface[]>(() => {
    return changedPlan ? [changedPlan] : []
  }, [changedPlan])

  const plans = useMemo<PlanInterface[]>(() => {
    if (!Array.isArray(plansWithMenu)) return []
    return plansWithMenu.filter(
      (plan) => plan.id !== planFromLp?.id && plan.id !== changedPlanId,
    )
  }, [plansWithMenu, planFromLp, changedPlanId])

  const doctorIntro = useMemo(() => {
    const userTreatmentId = Number(LocalStorage.getUserTreatmentId())
    const treatment = user?.user_treatments?.find(
      (treatments) => treatments.id === userTreatmentId,
    )
    const doctorName = treatment?.reservation?.doctor?.name || ''
    return `${doctorName}です。`
  }, [user])

  if (!plans) {
    return <div></div>
  }

  return (
    <>
      <LPHeader />
      <div className="w-full justify-center mx-auto proposal">
        <StepsHeader
          step={10}
          // stepActive={4}
          stepActiveName={'処方のご提案'}
          className="top-70"
        />
        {plansLoading ? (
          <Skeleton />
        ) : (
          <ProposalDiv className="proposal">
            <div className="proposal-content">
              <HeaderStyle>処方提案</HeaderStyle>
              <MenuNameComponent />
              <div className="proposal__content">
                <p>{doctorIntro}</p>
                <br />
                <p>
                  本サービスは、分割調剤の仕組みを利用しております。
                  <br />
                  分割調剤とは、初回の診察で一定期間分を処方するものの、一度にまとめてお送りすると服薬管理が大変になるため数回に分けてお送りする仕組みのことで、一定期間の処方をうけた場合に、お手持ちのお薬がなくなる前に決済を行い、その分のお薬を都度お届けさせていただきます。
                </p>
              </div>

              <div className="proposal__content-badge">
                <div className="proposal__content-badge-header">
                  <p className="proposal__content-badge-title">
                    診察の結果、あなたには以下のプランが処方可能です。
                    <br />
                    ご希望のプランを選択して、次に進んでください。
                  </p>
                </div>

                <PlanInfoComponent
                  plans={plans}
                  planImportants={planImportants}
                  planFromLp={
                    planFromLp?.id !== changedPlanId ? planFromLp : null
                  }
                  activePlan={activePlan}
                  changedPlanId={changedPlanId ?? undefined}
                  selectPlan={selectPlan}
                  planTypeBefore={PLAN_TYPE.BUY_SHAMPOO_SUBSCRIPTION}
                  showRecommendPlans={showRecommendPlans}
                  setShowRecommendPlans={setShowRecommendPlans}
                />

                <div className="pt-0 p-20">
                  <p>
                    定期便は定期の期間が終了した後に再問診が必要となります。
                  </p>
                  <p>
                    お体に合わない等、ご不明な点がございましたらメールにてカスタマーサポートへご相談ください。
                  </p>
                  <p>{EMAIL_SUPPORT}</p>
                </div>
              </div>
            </div>
          </ProposalDiv>
        )}
      </div>
    </>
  )
}

export default ShampooSubscriptionContainer
